import { useState } from 'react';
import { Brain, ChevronDown, Loader2, MessageCircle, ShieldCheck, Sparkles } from 'lucide-react';
import { Answer, Question } from '../types';
import { ConversationInsight as Insight, generateConversationInsight, InsightError, InsightMode } from '../lib/conversationInsight';
import { track } from '../lib/analytics';

interface Props {
  testId: string;
  question: Question;
  answer?: Answer;
  onUseStarter?: (text: string) => void;
  /** true when the recipient allowed a follow-up and the sender hasn't used it yet */
  followUpAvailable?: boolean;
}

const modes: { id: InsightMode; label: string; hint: string }[] = [
  { id: 'understand', label: 'Understand this answer', hint: 'A balanced read of what they said' },
  { id: 'next_question', label: 'What should I ask next?', hint: 'A respectful question to go deeper' },
  { id: 'reply_calmly', label: 'Help me reply calmly', hint: 'A low-conflict way to respond' },
];

export default function ConversationInsight({ testId, question, answer, onUseStarter, followUpAvailable = false }: Props) {
  const [mode, setMode] = useState<InsightMode>('understand');
  const [insight, setInsight] = useState<Insight | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState('');

  const requestInsight = async () => {
    if (busy || !answer?.partnerAnswer?.trim() || answer.skipped) return;
    setOpen(true);
    setBusy(true);
    setError('');
    track('conversation_insight_opened', { mode }, testId);
    try {
      const result = await generateConversationInsight({
        testId,
        questionText: question.text,
        expectedAnswer: question.expectedAnswer,
        partnerAnswer: answer.partnerAnswer,
        responseTimeMs: answer.responseTimeMs,
        editCount: answer.editCount,
        answerLength: answer.answerLength,
        mode,
      });
      setInsight(result);
      track('conversation_insight_generated', { mode }, testId);
    } catch (e) {
      console.error('Conversation Insight failed:', e);
      const known = e instanceof InsightError ? e : null;
      setError(known?.userMessage ?? 'Insight could not be generated right now. Your answer is still safe.');
      track('conversation_insight_failed', { mode, code: known?.code ?? 'unknown', status: known?.status ?? 0 }, testId);
    } finally {
      setBusy(false);
    }
  };

  const selectMode = (next: InsightMode) => {
    setMode(next);
    setInsight(null);
    setError('');
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older browsers / non-secure contexts: fall back to a hidden textarea
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
  };

  const useStarter = async (text: string) => {
    if (followUpAvailable) {
      onUseStarter?.(text);
      setNotice('Added to your follow-up box below. You can edit it before sending.');
      track('conversation_starter_selected', { mode, action: 'follow_up' }, testId);
    } else {
      await copyText(text);
      setNotice('Copied! You can paste it wherever you chat with them.');
      track('conversation_starter_selected', { mode, action: 'copy' }, testId);
    }
    window.setTimeout(() => setNotice(''), 4000);
  };

  if (!answer?.partnerAnswer?.trim() || answer.skipped) return null;

  return (
    <div className="mt-4 rounded-2xl border border-violet-500/20 bg-violet-500/5 overflow-hidden">
      <button
        type="button"
        onClick={() => { setOpen(v => !v); if (!open) track('conversation_insight_opened', { mode, expanded: true }, testId); }}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-violet-500/10 transition-colors"
      >
        <span className="w-8 h-8 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
          <Brain className="w-4 h-4 text-violet-400" />
        </span>
        <span className="flex-1">
          <span className="block text-[10px] font-black uppercase tracking-[0.2em] text-violet-400">Conversation Insight</span>
          <span className="block text-xs text-zinc-500 mt-0.5">Understand the answer without jumping to conclusions</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="grid gap-2 mb-3">
            {modes.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => selectMode(item.id)}
                className={`text-left rounded-xl border px-3 py-2 transition-colors ${mode === item.id ? 'border-violet-400/50 bg-violet-500/10' : 'border-brand-border bg-brand-bg hover:border-violet-400/30'}`}
              >
                <span className="block text-xs font-bold text-brand-text">{item.label}</span>
                <span className="block text-[11px] text-zinc-500 mt-0.5">{item.hint}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={requestInsight}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white py-3 text-xs font-black uppercase tracking-widest disabled:opacity-60 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? 'Reflecting…' : insight ? 'Refresh insight' : 'Help me understand'}
          </button>

          {error && <p className="mt-3 text-xs text-rose-400">{error}</p>}

          {insight && (
            <div className="mt-4 space-y-3">
              <div className="rounded-xl bg-brand-bg border border-brand-border p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">What they said</p>
                <p className="text-sm text-brand-text leading-relaxed">{insight.summary}</p>
              </div>
              <div className="rounded-xl bg-brand-bg border border-brand-border p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">What it might mean</p>
                <ul className="space-y-1.5 text-sm text-zinc-400 list-disc pl-4">
                  {insight.possibleMeanings.map((meaning, i) => <li key={i}>{meaning}</li>)}
                </ul>
              </div>
              <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-amber-400">Do not assume</p>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{insight.doNotAssume}</p>
              </div>
              {insight.conversationStarters.length > 0 && (
                <div className="rounded-xl bg-brand-bg border border-brand-border p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-3.5 h-3.5 text-violet-400" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-violet-400">Try saying</p>
                  </div>
                  {!followUpAvailable && (
                    <p className="text-xs text-zinc-500 mb-2 leading-relaxed">
                      They haven't allowed a follow-up yet. You can copy this text instead.
                    </p>
                  )}
                  <div className="space-y-2">
                    {insight.conversationStarters.map((starter, i) => (
                      <button key={i} type="button" onClick={() => useStarter(starter.text)} className="w-full text-left rounded-lg border border-brand-border px-3 py-2 hover:border-violet-400/40 hover:bg-violet-500/5 transition-colors">
                        <span className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">{starter.label}</span>
                        <span className="block text-sm text-brand-text leading-relaxed">“{starter.text}”</span>
                        <span className="block text-[10px] text-violet-400 font-bold mt-1">{followUpAvailable ? 'Use as follow-up' : 'Copy'}</span>
                      </button>
                    ))}
                  </div>
                  {notice && <p role="status" className="mt-2 text-xs font-bold text-violet-400">{notice}</p>}
                </div>
              )}
              <p className="text-[10px] text-zinc-600 leading-relaxed">Yhuu offers reflection, not a lie detector. A single answer cannot prove someone’s intentions.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
